import React, { useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { useSetRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";

export interface IAttachmentFile {
  id?: string;
  file?: File;

  fileName: string;
  size?: number;

  downloadUrl?: string;
  storagePath?: string;

  isUploaded?: boolean;

  contentType?: string;
  uploadedAt?: string;
  uploadedBy?: string;
}

interface Props {
  value: IAttachmentFile[];
  onChange: (files: IAttachmentFile[]) => void;

  disabled?: boolean;

  acceptedTypes?: string[];
  maxFileSizeMB?: number;
}

export default function AttachmentUploader({
  value = [],
  onChange,
  disabled = false,
  acceptedTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"],
  maxFileSizeMB = 10,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const setNotifications = useSetRecoilState(notificationsState);

  const handleSelectFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    const validFiles = selectedFiles.filter((file) => {
      const validSize = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!validSize) {
        setNotifications((prev) => [
          ...prev,
          { type: "error", message: `File size must not exceed ${maxFileSizeMB} MB.` },
        ]);
      }

      const validType =
        acceptedTypes.length === 0 || acceptedTypes.includes(file.type);

      if (!validType) {
        setNotifications((prev) => [
          ...prev,
          { type: "error", message: "Invalid file type." },
        ]);
      }

      return validSize && validType;
    });

    const mappedFiles: IAttachmentFile[] = validFiles.map((file) => ({
      file,
      fileName: file.name,
      size: file.size,
      isUploaded: false,
    }));

    onChange([...value, ...mappedFiles]);

    event.target.value = "";
  };

  const removeFile = (index: number) => {
    const updated = [...value];

    updated.splice(index, 1);

    onChange(updated);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography>Employee Attachments</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              Upload Attachments
            </Button>

            <input
              ref={inputRef}
              hidden
              multiple
              type="file"
              accept={acceptedTypes.join(",")}
              onChange={handleSelectFiles}
            />
          </Box>
        </Stack>

        <List disablePadding>
          {value.map((file, index) => (
            <ListItem
              key={`${file.fileName}-${index}`}
              divider={index !== value.length - 1}
              secondaryAction={
                <Stack direction="row" spacing={1}>
                  {(file.downloadUrl || file.file) && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        const url =
                          file.downloadUrl || URL.createObjectURL(file.file!);
                        window.open(url, "_blank");
                      }}
                    >
                      <RemoveRedEyeOutlinedIcon />
                    </IconButton>
                  )}

                  {!disabled && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeFile(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              }
            >
              {(file.downloadUrl || file.file) && (
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid",
                    borderColor: "divider",
                    mr: 2,
                  }}
                >
                  {file.file?.type.startsWith("image/") ||
                  file.contentType?.startsWith("image/") ? (
                    <img
                      src={file.downloadUrl || URL.createObjectURL(file.file!)}
                      alt={file.fileName}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <DescriptionIcon />
                  )}
                </Box>
              )}

              <ListItemText
                primary={
                  <Typography fontSize={"12px"}>{file.fileName}</Typography>
                }
                secondary={
                  file.size ? (
                    <Typography
                      color={"primary"}
                      fontSize={"12px"}
                    >{`${(file.size / 1024 / 1024).toFixed(2)} MB`}</Typography>
                  ) : (
                    ""
                  )
                }
              />
            </ListItem>
          ))}
        </List>
      </Stack>
    </Paper>
  );
}
